import React, { createContext, useContext } from 'react'
import { Basket, BasketItem, AddToBasketRequest, Product } from '@/models/shop.ts'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useClient } from '@/stores/useClient.ts'
import { useParams } from 'react-router-dom'

export interface BasketActions {
  refetch: () => Promise<any>
  addItem: (item: AddToBasketRequest) => Promise<void>
  updateItem: (itemId: string, quantity: number) => Promise<void>
  removeItem: (itemId: string) => Promise<void>
  clearBasket: () => Promise<void>
  getBasketItem(id: string): BasketItem | undefined
}

interface BasketActionsContext {
  basket: Basket | null
  isLoading: boolean
}

export const BasketActionsContext = createContext<BasketActionsContext & BasketActions>({
  basket: null,
  isLoading: false,
  getBasketItem(): BasketItem | undefined {
    return undefined
  },
  async refetch() {},
  async addItem() {},
  async updateItem() {},
  async removeItem() {},
  async clearBasket() {},
})

export function BasketActionsProvider({ children }: { children: React.ReactNode }) {
  const { getCurrentBasket, addToBasket, updateBasketItem, removeBasketItem, clearBasket } = useClient()
  const queryClient = useQueryClient()
  const queryKey = ['basket', 'current']
  const { driveId } = useParams<{ driveId: string }>()

  const { data, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await getCurrentBasket(driveId!)
      return res.data
    },
  })

  function removeBasketItemFromCache(itemId: string) {
    queryClient.setQueryData(queryKey, (prev: Basket | null) => {
      if (!prev) {
        return null
      }
      return {
        ...prev,
        items: prev.items.filter((item: BasketItem) => item.id !== itemId)
      }
    })
  }

  const addItemMutation = useMutation({
    mutationFn: async (item: AddToBasketRequest) => {
      return await addToBasket(item)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const updateItemMutation = useMutation({
    mutationFn: async ({ itemId, quantity }: { itemId: string, quantity: number }) => {
      await updateBasketItem({ item_id: itemId, quantity })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const removeItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      await removeBasketItem(itemId)
    },
    onSuccess: (_, itemId) => {
      removeBasketItemFromCache(itemId)
    },
  })

  const clearBasketMutation = useMutation({
    mutationFn: async () => {
      await clearBasket()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  function getBasketItem(id: string): BasketItem | undefined {
    return data?.items?.find((item: BasketItem) => item.id === id)
  }

  return <BasketActionsContext.Provider value={{
    basket: data || null,
    getBasketItem,
    refetch,
    isLoading,
    // @ts-ignore idk
    addItem: addItemMutation.mutateAsync,
    updateItem: (itemId: string, quantity: number) => updateItemMutation.mutateAsync({ itemId, quantity }),
    removeItem: removeItemMutation.mutateAsync,
    clearBasket: clearBasketMutation.mutateAsync,
  }}>
    { children }
  </BasketActionsContext.Provider>
}

export function useBasket() {
  return useContext(BasketActionsContext)
}

export function modParamsFromURL() {
  return new URLSearchParams(location.search)
}

export function modParamsToAPI(modParams: URLSearchParams, product: Product) {
  const modsForAPI: Record<string, string> = {}

  // Convert tech_ids from URL to property/value IDs
  modParams.forEach((valueTechId, propertyTechId) => {
    // Find the property by tech_id
    const property = product.properties.find(p => p.is_mod && p.tech_id === propertyTechId)
    if (property) {
      // Find the value by tech_id
      const value = property.values.find(v => v.tech_id === valueTechId)
      if (value) {
        modsForAPI[property.id] = value.id
      }
    }
  })

  return modsForAPI
}
